"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import type { PortfolioState } from "../../lib/portfolio-types";
import type { TimeRangeValue } from "../../lib/time-range";
import { workspaceRouteKey } from "../../lib/workspace-navigation.ts";
import {
  isPositionsAllocationPageModel,
  isWorkspacePageModel,
  type WorkspacePageModelEnvelope,
} from "../../lib/workspace-read-models.ts";
import { LoadingActivityPanel } from "./ui/LoadingActivityPanel";
import { LoadingState } from "./ui/LoadingState";
import { markWorkspacePerformance, workspacePerformanceMarks } from "../../frontend/src/workspace-performance";

const WealthApp = dynamic(
  () => import("./WealthApp").then((module) => module.WealthApp),
  { ssr: false },
);
const AllocationRoute = dynamic(
  () => import("./workspace/AllocationRoute").then((module) => module.AllocationRoute),
  { ssr: false },
);

type PortfolioBootstrapProps = {
  user: { email: string; displayName: string };
  initialView?: string;
  initialSection?: string;
  initialTimeRange: TimeRangeValue;
  initialPeriod?: string;
  initialStateRequest?: Promise<PortfolioPageStateLoad> | null;
};

export type PortfolioPageStateLoad =
  | { ok: true; payload: PortfolioState | WorkspacePageModelEnvelope }
  | { ok: false; error: Error };

export function pageStateUrl(input: Pick<PortfolioBootstrapProps, "initialView" | "initialSection" | "initialTimeRange">) {
  const legacyPlanning = input.initialView === "planning";
  const view = legacyPlanning ? "positions" : input.initialView ?? "overview";
  const section = legacyPlanning || input.initialSection === "groups" ? "allocation" : input.initialSection;
  const routeKey = workspaceRouteKey(view, section);
  const params = new URLSearchParams({
    action: "page_model",
    routeKey,
    view,
    range: input.initialTimeRange.preset,
  });
  if (routeKey === "positions-allocation") {
    if (typeof window !== "undefined") {
      const current = new URLSearchParams(window.location.search);
      for (const key of ["scope", "scopeId", "period", "anchor", "display"] as const) {
        const value = current.get(key);
        if (value) params.set(key, value);
      }
    }
  }
  if (section) params.set("section", section);
  if (input.initialTimeRange.from) params.set("from", input.initialTimeRange.from);
  if (input.initialTimeRange.to) params.set("to", input.initialTimeRange.to);
  return `/api/portfolio?${params.toString()}`;
}

export async function requestPortfolioPageState(
  input: Pick<PortfolioBootstrapProps, "initialView" | "initialSection" | "initialTimeRange">,
  signal?: AbortSignal,
): Promise<PortfolioPageStateLoad> {
  try {
    const response = await fetch(pageStateUrl(input), { cache: "no-store", signal });
    const text = await response.text();
    let payload: (PortfolioState & { error?: string }) | WorkspacePageModelEnvelope | null = null;
    try { payload = JSON.parse(text) as (PortfolioState & { error?: string }) | WorkspacePageModelEnvelope; } catch { payload = null; }
    if (!response.ok || !payload) {
      const error = payload && "error" in payload ? payload.error : null;
      throw new Error(error || `资产数据请求失败（HTTP ${response.status}）`);
    }
    if ("contractVersion" in payload && !isWorkspacePageModel(payload)) throw new Error("页面数据读取合同版本不兼容");
    return { ok: true, payload };
  } catch (loadError) {
    return {
      ok: false,
      error: loadError instanceof Error ? loadError : new Error("资产数据加载失败"),
    };
  }
}

export function PortfolioBootstrap(props: PortfolioBootstrapProps) {
  const [payload, setPayload] = useState<PortfolioState | WorkspacePageModelEnvelope | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const rangePreset = props.initialTimeRange.preset;
  const rangeFrom = props.initialTimeRange.from;
  const rangeTo = props.initialTimeRange.to;

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        const request = attempt === 0 && props.initialStateRequest
          ? props.initialStateRequest
          : requestPortfolioPageState({
            initialView: props.initialView,
            initialSection: props.initialSection,
            initialTimeRange: { preset: rangePreset, from: rangeFrom, to: rangeTo },
          }, controller.signal);
        const result = await request;
        if (!result.ok) throw result.error;
        markWorkspacePerformance(workspacePerformanceMarks.routeDataReady);
        setPayload(result.payload);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setError(loadError instanceof Error ? loadError.message : "资产数据加载失败");
      }
    }
    void load();
    return () => controller.abort();
  }, [attempt, props.initialSection, props.initialStateRequest, props.initialView, rangeFrom, rangePreset, rangeTo]);

  if (payload) {
    if (isWorkspacePageModel(payload)) {
      if (isPositionsAllocationPageModel(payload)) return <AllocationRoute model={payload} user={props.user} />;
      return <WealthApp
        initialPageModel={payload}
        initialCompact
        initialView={props.initialView}
        initialSection={props.initialSection}
        initialTimeRange={props.initialTimeRange}
        initialPeriod={props.initialPeriod}
        user={props.user}
      />;
    }
    return <WealthApp
      initialState={payload}
      initialCompact
      initialView={props.initialView}
      initialSection={props.initialSection}
      initialTimeRange={props.initialTimeRange}
      initialPeriod={props.initialPeriod}
      user={props.user}
    />;
  }

  return <main className="ledger-route-loading" data-bootstrap-state={error ? "failed" : "loading"}>
    <LoadingState
      label={error ? "家庭资产数据暂未载入" : "家庭资产数据"}
      detail={error ? "页面已经打开，可重试独立的数据请求" : "正在从 PostgreSQL 读取账本并建立投资视图"}
    />
    {error ? <section className="qy-bootstrap-error" role="alert">
      <strong>数据请求失败</strong>
      <p>{error}</p>
      <button type="button" onClick={() => { setError(""); setAttempt((value) => value + 1); }}>重新加载数据</button>
      <small>请截图此信息；刷新或重试不会修改资产数据。</small>
    </section> : <LoadingActivityPanel operation="读取家庭资产数据" waitingFor="PostgreSQL 返回并计算投资视图" />}
  </main>;
}
